/**
 * Search Slide Component
 * Handles the sliding search input functionality with dropdown results
 */

import { Component } from '@theme/component';

class SearchSlideComponent extends Component {
  constructor() {
    super();
    this.isActive = false;
    this.resultsVisible = false;
    this.searchTimeout = null;
    this.selectedResultIndex = -1;
    
    // Bind event handlers for global events
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    
    console.log('SearchSlideComponent connected');
    console.log('Available refs:', Object.keys(this.refs));
    
    // Add global event listeners for document-level events
    document.addEventListener('click', this.boundHandleDocumentClick);
    document.addEventListener('keydown', this.boundHandleEscapeKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Remove global event listeners
    document.removeEventListener('click', this.boundHandleDocumentClick);
    document.removeEventListener('keydown', this.boundHandleEscapeKey);
  }

  toggleSearch() {
    console.log('toggleSearch called, isActive:', this.isActive);
    console.log('searchContainer ref:', this.refs.searchContainer);
    
    if (this.isActive) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    console.log('openSearch called, isActive:', this.isActive);
    if (this.isActive) return;

    this.isActive = true;
    this.classList.add('search-slide--active');
    
    console.log('Setting searchContainer.hidden = false');
    // Show the search container using refs
    this.refs.searchContainer.hidden = false;
    
    // Force reflow to ensure the element is rendered
    this.refs.searchContainer.offsetHeight;
    
    console.log('Adding active class for animation');
    // Add active class for animation
    this.refs.searchContainer.classList.add('search-slide__container--active');
    
    // Focus the input after animation
    setTimeout(() => {
      this.refs.searchInput.focus();
    }, 100);
  }

  closeSearch() {
    if (!this.isActive) return;

    this.isActive = false;
    this.classList.remove('search-slide--active');
    
    // Clear search input using refs
    this.refs.searchInput.value = '';
    
    // Hide results dropdown
    this.hideResults();
    
    // Remove active class for animation
    this.refs.searchContainer.classList.remove('search-slide__container--active');
    
    // Hide the container after animation
    setTimeout(() => {
      this.refs.searchContainer.hidden = true;
    }, 300);
  }

  handleSearchInput(event) {
    const query = event.target.value.trim();
    
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // If query is empty, hide results
    if (!query) {
      this.hideResults();
      return;
    }
    
    // Debounce search requests with 300ms delay
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  handleInputFocus() {
    // Keep input focused, don't interfere with dropdown
  }

  handleInputBlur(event) {
    // Don't close on blur, let document click handle it
    // This prevents the dropdown from closing when clicking on results
  }

  handleInputKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeSearch();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateResults(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateResults(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectCurrentResult();
        break;
    }
  }

  handleFormSubmit(event) {
    const query = this.refs.searchInput.value.trim();
    if (!query) {
      event.preventDefault();
      return;
    }
    
    // Allow form to submit naturally for search results page
    this.closeSearch();
  }

  handleDocumentClick(event) {
    // Close search if clicking outside
    if (this.isActive && !this.contains(event.target)) {
      this.closeSearch();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isActive) {
      this.closeSearch();
    }
  }

  async performSearch(query) {
    try {
      // Show loading state
      this.showLoadingState();
      
      // Show results dropdown
      this.showResults();
      
      // Trigger predictive search
      await this.triggerPredictiveSearch(query);
      
    } catch (error) {
      console.error('Search error:', error);
      this.hideLoadingState();
      this.showEmptyState();
    }
  }

  async triggerPredictiveSearch(query) {
    try {
      // Use the same approach as the existing predictive search
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');
      
      const response = await fetch(url);
      const html = await response.text();
      
      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the results content - try multiple selectors
      const resultsContent = doc.querySelector('.predictive-search-dropdown') || 
                           doc.querySelector('#predictive-search-results') ||
                           doc.querySelector('[id*="predictive-search"]');
      
      if (resultsContent && resultsContent.innerHTML.trim()) {
        this.renderResults(resultsContent.outerHTML);
      } else {
        this.showEmptyState();
      }
      
      this.hideLoadingState();
      
    } catch (error) {
      console.error('Predictive search error:', error);
      this.hideLoadingState();
      this.showEmptyState();
    }
  }

  showResults() {
    if (!this.refs.resultsDropdown) return;
    
    this.resultsVisible = true;
    this.refs.resultsDropdown.hidden = false;
    
    // Force reflow
    this.refs.resultsDropdown.offsetHeight;
    
    // Add active class for animation
    this.refs.resultsDropdown.classList.add('search-slide__results-dropdown--active');
    
    // Keep focus on search input
    this.refs.searchInput.focus();
  }

  hideResults() {
    if (!this.refs.resultsDropdown) return;
    
    this.resultsVisible = false;
    this.selectedResultIndex = -1;
    
    // Remove active class for animation
    this.refs.resultsDropdown.classList.remove('search-slide__results-dropdown--active');
    
    // Hide after animation
    setTimeout(() => {
      this.refs.resultsDropdown.hidden = true;
      this.refs.loadingState.hidden = true;
      this.refs.emptyState.hidden = true;
    }, 200);
  }

  showLoadingState() {
    if (!this.refs.loadingState) return;
    
    this.refs.loadingState.hidden = false;
    this.refs.resultsContainer.innerHTML = '';
    this.refs.emptyState.hidden = true;
  }

  hideLoadingState() {
    if (!this.refs.loadingState) return;
    
    this.refs.loadingState.hidden = true;
  }

  showEmptyState() {
    if (!this.refs.emptyState) return;
    
    this.refs.emptyState.hidden = false;
    this.refs.resultsContainer.innerHTML = '';
  }

  renderResults(html) {
    if (!this.refs.resultsContainer) return;
    
    this.refs.resultsContainer.innerHTML = html;
    this.refs.emptyState.hidden = true;
    
    // Add click handlers to results
    this.addResultClickHandlers();
  }

  addResultClickHandlers() {
    const results = this.refs.resultsContainer.querySelectorAll('a');
    results.forEach((result, index) => {
      result.addEventListener('click', () => {
        this.closeSearch();
      });
    });
  }

  navigateResults(direction) {
    if (!this.resultsVisible) return;
    
    const results = this.refs.resultsContainer.querySelectorAll('a');
    if (results.length === 0) return;
    
    // Update selected index
    this.selectedResultIndex += direction;
    
    // Clamp to valid range
    if (this.selectedResultIndex < -1) {
      this.selectedResultIndex = results.length - 1;
    } else if (this.selectedResultIndex >= results.length) {
      this.selectedResultIndex = -1;
    }
    
    // Update visual states
    results.forEach((result, index) => {
      if (index === this.selectedResultIndex) {
        result.classList.add('search-slide__result-item--selected');
        result.focus();
      } else {
        result.classList.remove('search-slide__result-item--selected');
      }
    });
    
    // If no result selected, focus back on input
    if (this.selectedResultIndex === -1) {
      this.refs.searchInput.focus();
    }
  }

  selectCurrentResult() {
    if (this.selectedResultIndex >= 0) {
      const results = this.refs.resultsContainer.querySelectorAll('a');
      const selectedResult = results[this.selectedResultIndex];
      if (selectedResult) {
        selectedResult.click();
      }
    } else {
      // Submit form if no result selected
      this.refs.form.submit();
    }
  }
}

// Register the custom element
customElements.define('search-slide-component', SearchSlideComponent);